/**
 * Safe wrapper around Auth0's useUser hook
 * Returns mock user data in dev mode to avoid API calls to /auth/profile
 * 
 * React 19 Best Practices:
 * - Hooks must be called unconditionally, so we always call useUser() but handle dev mode
 * - In dev mode, we return mock data immediately to prevent unnecessary API calls
 */

'use client';

import { useUser as useAuth0User } from '@auth0/nextjs-auth0/client';
import { isDevMode, MOCK_USER } from './dev-mode';
import { useMemo } from 'react';

/**
 * Safe wrapper around Auth0's useUser hook
 * In dev mode, returns mock user data immediately without making API calls
 * In production, uses Auth0's useUser hook normally
 */
export function useUserSafe() {
  // Always call the hook (React rules), but we'll override the return value in dev mode
  const auth0Result = useAuth0User();
  
  // In dev mode, return mock data immediately to avoid API calls
  // This prevents 404 errors for /auth/profile in dev mode
  return useMemo(() => {
    if (isDevMode()) {
      return {
        user: MOCK_USER,
        error: undefined,
        isLoading: false,
        checkSession: async () => {},
      };
    }
    
    // In production, return Auth0's result
    return auth0Result;
  }, [auth0Result]);
}

